import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ESignatureDialog from "@/components/ESignatureDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GraduationCap, Plus, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Training() {
  const { user } = useAuth();
  const [tab, setTab] = useState("courses");
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [cOpen, setCOpen] = useState(false);
  const [aOpen, setAOpen] = useState(false);
  const [cForm, setCForm] = useState({ title: "", description: "", duration_min: 30, quiz_required: false });
  const [aForm, setAForm] = useState({ course_id: "", assignee_email: "", due_date: "" });
  const [esignOpen, setEsignOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const load = async () => {
    const [c, a] = await Promise.all([api.get("/training/courses"), api.get("/training/assignments")]);
    setCourses(c.data); setAssignments(a.data);
  };
  useEffect(() => { load(); }, []);

  const createCourse = async () => {
    if (!cForm.title) return toast.error("Title required");
    try {
      await api.post("/training/courses", cForm);
      toast.success("Course created");
      setCOpen(false);
      setCForm({ title: "", description: "", duration_min: 30, quiz_required: false });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && e.response?.status !== 403 && e.response?.status < 500) {
        toast.error(typeof detail === "string" ? detail : "Create failed");
      }
    }
  };

  const createAssignment = async () => {
    if (!aForm.course_id || !aForm.assignee_email) return toast.error("Course and assignee required");
    try {
      await api.post("/training/assignments", aForm);
      toast.success("Assigned");
      setAOpen(false);
      setAForm({ course_id: "", assignee_email: "", due_date: "" });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && e.response?.status !== 403 && e.response?.status < 500) {
        toast.error(typeof detail === "string" ? detail : "Assign failed");
      }
    }
  };

  const complete = (a) => {
    setPending(a);
    setEsignOpen(true);
  };

  const onSigned = async (sig) => {
    try {
      await api.post("/training/complete", { assignment_id: pending.assignment_id, signature_id: sig.signature_id });
      toast.success("Training completed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setPending(null);
  };

  const myAssignments = assignments.filter((a) => a.assignee_email === user?.email);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="vq-overline mb-2">Training & Competency</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">Training / LMS</h1>
        <p className="text-slate-600 mb-8">Role-based training with e-signature-attested completion (21 CFR Part 11)</p>

        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {[{ v: "courses", label: "Courses" }, { v: "assignments", label: "All assignments" }, { v: "my", label: `My training (${myAssignments.length})` }].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`tr-tab-${t.v}`}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.v ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "courses" && (
          <>
            <div className="flex justify-end mb-4">
              <Button onClick={() => setCOpen(true)} data-testid="tr-course-new" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                <Plus className="w-4 h-4 mr-2" /> New Course
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {courses.map((c) => (
                <div key={c.course_id} className="vq-card p-5">
                  <GraduationCap className="w-6 h-6 text-[#0055FF] mb-3" />
                  <div className="font-display font-bold text-slate-900">{c.title}</div>
                  <div className="text-xs text-slate-600 mt-2">{c.description}</div>
                  <div className="flex items-center gap-2 mt-4 text-[10px] font-mono text-slate-500">
                    <Clock className="w-3 h-3" /> {c.duration_min} min {c.quiz_required && "• Quiz"}
                  </div>
                </div>
              ))}
              {courses.length === 0 && <div className="col-span-full text-center py-12 text-slate-500">No courses.</div>}
            </div>
          </>
        )}

        {(tab === "assignments" || tab === "my") && (
          <>
            {tab === "assignments" && (
              <div className="flex justify-end mb-4">
                <Button onClick={() => setAOpen(true)} data-testid="tr-assign-new" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                  <Plus className="w-4 h-4 mr-2" /> Assign Training
                </Button>
              </div>
            )}
            <div className="vq-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Course</th>
                    <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Assignee</th>
                    <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Due</th>
                    <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(tab === "my" ? myAssignments : assignments).map((a) => {
                    const course = courses.find((c) => c.course_id === a.course_id);
                    return (
                      <tr key={a.assignment_id} className="border-b border-slate-100">
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                          {course?.title || a.course_id}
                          {a.linked_document_id && (
                            <div className="text-[10px] font-mono text-slate-500 font-normal mt-0.5 flex items-center gap-1.5">
                              <span>Linked SOP: {a.linked_document_title || a.linked_document_id}</span>
                              <span>· signed v{a.linked_document_version_signed || a.linked_document_version_at_assignment || "?"}</span>
                              {a.sop_updated && (
                                <span
                                  data-testid={`tr-sop-updated-${a.assignment_id}`}
                                  title="Linked SOP has been revised since this training. Retraining recommended."
                                  className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5"
                                >
                                  SOP updated
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs font-mono text-slate-600">{a.assignee_email}</td>
                        <td className="px-6 py-4 text-xs font-mono text-slate-600">{a.due_date || "—"}</td>
                        <td className="px-6 py-4">
                          {a.status === "Completed" ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-mono">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                            </span>
                          ) : (
                            <span className="text-amber-700 text-xs font-mono">Assigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {a.assignee_email === user?.email && a.status !== "Completed" && (
                            <Button size="sm" onClick={() => complete(a)} data-testid={`tr-complete-${a.assignment_id}`}
                                    className="bg-slate-900 hover:bg-[#0055FF] text-white">
                              Complete with e-sig
                            </Button>
                          )}
                          {a.status === "Completed" && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`tr-cert-${a.assignment_id}`}
                              onClick={() => {
                                // Fresh cookie-authenticated download. Using window.open keeps the
                                // main tab intact so the user doesn't lose their place in the list.
                                const url = `${process.env.REACT_APP_BACKEND_URL}/api/training/certificate/${a.assignment_id}`;
                                window.open(url, "_blank", "noopener,noreferrer");
                              }}
                              className="border-slate-300 text-slate-900 font-semibold"
                              title="Download PDF certificate"
                            >
                              Certificate ↓
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {(tab === "my" ? myAssignments : assignments).length === 0 && (
                    <tr><td colSpan={5} className="text-center py-12 text-slate-500">No assignments.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">New Course</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Title</Label>
              <Input data-testid="tr-course-title" value={cForm.title} onChange={(e) => setCForm({ ...cForm, title: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Description</Label>
              <Textarea value={cForm.description} onChange={(e) => setCForm({ ...cForm, description: e.target.value })} rows={3} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Duration (min)</Label>
              <Input type="number" value={cForm.duration_min} onChange={(e) => setCForm({ ...cForm, duration_min: parseInt(e.target.value) || 0 })} className="mt-1.5" /></div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={cForm.quiz_required} onCheckedChange={(v) => setCForm({ ...cForm, quiz_required: !!v })} />
              Quiz required
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCOpen(false)}>Cancel</Button>
            <Button onClick={createCourse} data-testid="tr-course-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={aOpen} onOpenChange={setAOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Assign Training</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Course</Label>
              <select data-testid="tr-assign-course" value={aForm.course_id} onChange={(e) => setAForm({ ...aForm, course_id: e.target.value })} className="mt-1.5 w-full h-10 border border-slate-300 rounded px-3 text-sm">
                <option value="">Select course</option>
                {courses.map((c) => (<option key={c.course_id} value={c.course_id}>{c.title}</option>))}
              </select></div>
            <div><Label className="text-xs font-semibold">Assignee email</Label>
              <Input data-testid="tr-assign-email" value={aForm.assignee_email} onChange={(e) => setAForm({ ...aForm, assignee_email: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Due date</Label>
              <Input type="date" value={aForm.due_date} onChange={(e) => setAForm({ ...aForm, due_date: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAOpen(false)}>Cancel</Button>
            <Button onClick={createAssignment} data-testid="tr-assign-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pending && (
        <ESignatureDialog
          open={esignOpen} onOpenChange={setEsignOpen}
          entityType="training_assignment" entityId={pending.assignment_id}
          action="training_completion"
          defaultMeaning="Training Completion"
          title="Attest training completion"
          description="By signing, you attest that you have completed and understood the training material."
          onSigned={onSigned}
        />
      )}
    </DashboardLayout>
  );
}
